<template>
  <div class="d-flex align-items-center">
    <b-form-checkbox
      :id="`device-${device.id}`"
      :name="`device-${device.id}`"
      :disabled="localLoading"
      :checked="device.status === 'ARMED'"
      switch
      @change="handleToggle"
      v-if="isVisible && hasOnlyBasicActions"
    >
    </b-form-checkbox>

    <b-dropdown
      v-if="isVisible && !hasOnlyBasicActions"
      :text="currentActionText"
      :disabled="localLoading"
      variant="outline-primary"
      size="sm"
      class="area-actions-dropdown"
      no-caret
      boundary="window"
    >
      <template #button-content>
        <i class="feather icon-settings mr-1"></i>
        {{ t("bosch.devices.area_status") }}
      </template>

      <b-dropdown-item
        v-for="(action, index) in availableActions"
        :key="`action-${device.id}-${index}-${action.name}`"
        @click.stop="executeAction(action)"
        :disabled="localLoading"
        class="d-flex align-items-center w-100"
      >
        <i :class="getActionIcon(action.name)" class="mr-2"></i>
        {{ getActionDisplayName(action.name) }}
      </b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import { useDeviceMessages } from "./composables/useDeviceMessages";
import axios from "@axios";
import { useUtils as useAclUtils } from "@core/libs/acl";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { BDropdown, BDropdownItem, BFormCheckbox } from "bootstrap-vue";

export default {
  name: "BoschArea",
  components: {
    BDropdown,
    BDropdownItem,
    BFormCheckbox,
  },
  props: {
    device: Object,
    trans: Object,
    loadingToast: Object,
    updateStatus: Function,
    timeout: Number,
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props) {
    const { confirmActionAlert, successAlertAction, errorAlertAction, showToast } =
      useDeviceMessages(props.trans);
    const localLoading = ref(false);
    const { can } = useAclUtils();
    const { t } = useI18nUtils();

    const hasOnlyBasicActions = computed(() => {
      if (!props.device.actions || props.device.actions.length <= 2) return true;

      const basicActions = ["Disarm"];
      const armActions = [
        "Master Instant Arm",
        "Master Delay Arm",
        "Perimeter Instant Arm",
        "Perimeter Delay Arm",
      ];

      const hasDisarm = props.device.actions.some((action) =>
        basicActions.includes(action.name)
      );
      const hasArm = props.device.actions.some((action) =>
        armActions.includes(action.name)
      );

      return hasDisarm && hasArm && props.device.actions.length === 2;
    });

    const availableActions = computed(() => {
      if (!props.device.actions) return [];
      return props.device.actions.filter((action) => {
        if (action.name === "Disarm") {
          return can("disarm_area", "BoschPanel");
        } else if (action.name.includes("Arm")) {
          return can("arm_area", "BoschPanel");
        }
        return true;
      });
    });

    const currentActionText = computed(() => {
      return t("bosch.devices.area_status");
    });

    const getActionDisplayName = (actionName) => {
      return actionName;
    };

    const getActionIcon = (actionName) => {
      if (actionName === "Disarm") {
        return "feather icon-shield-off text-warning";
      } else if (actionName.includes("Force")) {
        return "feather icon-alert-triangle text-danger";
      } else if (actionName.includes("Perimeter")) {
        return "feather icon-circle text-info";
      } else if (actionName.includes("Master")) {
        return "feather icon-shield text-success";
      }
      return "feather icon-settings";
    };

    const handleToggle = async () => {
      const result = await confirmActionAlert(props.device);

      if (!result.isConfirmed) {
        document.getElementById(`device-${props.device.id}`).checked =
          props.device.status === "ARMED";
        return;
      }

      localLoading.value = true;
      showToast(props.device.status, props.device, props.loadingToast);

      try {
        const action = props.device.status === "ARMED" ? 1 : 0;
        let urlAction = "";
        if (props.device.actions.length > 1) {
          urlAction = props.device.actions[action]?.data?.url;
        } else {
          urlAction = props.device.actions[0]?.data?.url;
        }

        await axios.get(urlAction);

        props.updateStatus();

        successAlertAction(props.device);

        setTimeout(() => {
          if (props.updateStatus) {
            props.updateStatus();
          }
        }, props.timeout);
      } catch (error) {
        document.getElementById(`device-${props.device.id}`).checked =
          props.device.status === "ARMED";
        console.error(
          `error in request for device ${props.device.text}:`,
          JSON.stringify(error)
        );
        errorAlertAction(props.device.status, error.response?.data?.message);
      } finally {
        localLoading.value = false;
        if (props.loadingToast) props.loadingToast.close();
      }
    };

    const executeAction = async (action) => {
      const result = await confirmActionAlert(props.device);

      if (!result.isConfirmed) {
        return;
      }

      localLoading.value = true;
      showToast(props.device.status, props.device, props.loadingToast);

      try {
        await axios.get(action.data.url);
        props.updateStatus();
        successAlertAction(props.device);

        setTimeout(() => {
          if (props.updateStatus) {
            props.updateStatus();
          }
        }, props.timeout);
      } catch (error) {
        console.error(
          `error in request for device ${props.device.text}:`,
          JSON.stringify(error)
        );
        errorAlertAction(props.device.status, error.response?.data?.message);
      } finally {
        localLoading.value = false;
        if (props.loadingToast) props.loadingToast.close();
      }
    };

    const titleAction = (status) =>
      status === "ARMED" ? props.trans["disarm"] : props.trans["arm"];

    const isVisible = computed(() => {
      if (can("disarm_area", "BoschPanel") && can("arm_area", "BoschPanel")) return true;
      if (props.device.status === "ARMED") {
        return can("disarm_area", "BoschPanel");
      } else if (props.device.status === "DISARMED") {
        return can("arm_area", "BoschPanel");
      }
    });

    return {
      handleToggle,
      localLoading,
      isVisible,
      hasOnlyBasicActions,
      availableActions,
      currentActionText,
      getActionDisplayName,
      getActionIcon,
      executeAction,
      t
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables/_variables.scss";

.area-actions-dropdown {
  position: relative;
  ::v-deep .btn {
    border: 1px solid #d8d6de;
    color: #495057;
    background-color: #ffffff;
    font-size: 0.857rem;
    padding: 0.5rem 1rem;
    transition: all 0.15s ease-in-out;

    &:hover {
      color: #495057;
      background-color: #e9ecef;
      border-color: #adb5bd;
    }

    &:focus {
      color: #495057;
      background-color: #e9ecef;
      border-color: #80bdff;
      outline: 0;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }

    .dark-layout & {
      border-color: $theme-dark-input-border-color;
      color: $theme-dark-label-color;
      background-color: $theme-dark-input-bg;

      &:hover {
        background-color: $theme-dark-table-hover-bg;
        border-color: $theme-dark-input-border-color;
        color: $theme-dark-headings-color;
      }

      &:focus {
        background-color: $theme-dark-table-hover-bg;
        border-color: $primary;
        color: $theme-dark-headings-color;
        box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
      }
    }
  }

  ::v-deep .dropdown-menu {
    border: 1px solid #d8d6de;
    box-shadow: $theme-dark-box-shadow;
    z-index: 1051 !important;
    position: absolute !important;
    transform: none !important;
    top: 100% !important;
    left: 0 !important;
    margin-top: 2px;
    min-width: 200px;

    .dark-layout & {
      background-color: $theme-dark-input-bg;
      border-color: $third-dark;
    }
  }

  ::v-deep .dropdown-item {
    padding: 0.75rem 1rem;
    font-size: 0.857rem;
    white-space: nowrap;

    &:hover {
      background-color: #f8f9fa;
    }

    .dark-layout & {
      color: $theme-dark-text-muted-color;

      &:hover {
        background-color: $theme-dark-table-hover-bg;
        color: $theme-dark-headings-color;
      }
    }
  }
}

// Ensure the dropdown container allows overflow
::v-deep .area-header,
::v-deep .device-row {
  overflow: visible !important;
}

// Position dropdown relative to button
::v-deep .b-dropdown {
  position: relative !important;
}

// Override any conflicting positioning
::v-deep .dropdown-menu.show {
  z-index: 1051 !important;
  position: absolute !important;
  will-change: transform;
}
</style>
